import { useState } from "react";
import { Mail, Heart, Instagram } from "lucide-react";
import { supabase } from "@/integrations/supabase/client";
import { Mascot } from "@/components/Mascot";
import { AppStoreBadge } from "@/components/AppStoreBadge";
import { SEO } from "@/components/SEO";

export default function Launch() {
  const [email, setEmail] = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    const trimmed = email.trim().toLowerCase();
    if (!trimmed || isSubmitting) return;

    setIsSubmitting(true);
    try {
      const { error } = await supabase
        .from("newsletter_signups")
        .insert({ email: trimmed });

      if (error && error.code === "23505") {
        // Already signed up — treat as success
      } else if (error) {
        throw error;
      }

      // Send confirmation email
      await supabase.functions.invoke("send-newsletter-confirmation", {
        body: { email: trimmed },
      });

      setSubmitted(true);
      setEmail("");
    } catch {
      // Silent fail — show generic state
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <main className="min-h-dvh bg-[#F5F0E8] flex flex-col items-center justify-center px-6 py-16">
      <SEO
        title="Grounded — Your Next Work Café is Waiting"
        description="Find cafés worth settling into — community-rated on WiFi, outlets, seating & more. Download Grounded on the App Store."
        path="/launch"
      />
      <div className="w-full max-w-md text-center space-y-10">
        {/* Logo + Mascot */}
        <div className="flex flex-col items-center gap-3">
          <Mascot mood="relaxed" size="lg" priority />
          <h1
            className="font-display text-4xl md:text-5xl font-bold"
            style={{ color: "#3b2314" }}
          >
            grounded
          </h1>
        </div>

        {/* Copy */}
        <div className="space-y-4">
          <h2
            className="font-display text-2xl md:text-3xl font-semibold leading-snug"
            style={{ color: "#3b2314" }}
          >
            Your next work café is waiting.
          </h2>
          <p
            className="text-base md:text-lg leading-relaxed"
            style={{ color: "#5c4033" }}
          >
            Grounded helps remote workers, freelancers, and students find cafés
            worth settling into — rated on WiFi, outlets, seating, and more.
          </p>
        </div>

        {/* App Store CTA */}
        <div className="flex justify-center">
          <AppStoreBadge className="w-[200px]" />
        </div>

        {/* Secondary newsletter signup */}
        {submitted ? (
          <div
            className="rounded-xl px-6 py-5 text-center animate-fade-in"
            style={{ backgroundColor: "#3b2314" }}
          >
            <p
              className="text-base font-medium"
              style={{ color: "#f5ecd7" }}
            >
              You're on the list. We'll see you at the café. ☕
            </p>
          </div>
        ) : (
          <div className="space-y-2">
            <p className="text-sm" style={{ color: "#6b5440" }}>
              Stay updated on new cafés & tips:
            </p>
            <form onSubmit={handleSubmit} className="flex gap-2">
              <div className="relative flex-1">
                <Mail
                  className="absolute left-3.5 top-1/2 -translate-y-1/2 h-4 w-4"
                  style={{ color: "#6b5440" }}
                />
                <input
                  type="email"
                  required
                  maxLength={255}
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="your@email.com"
                  className="w-full pl-10 pr-4 py-3 rounded-xl border text-sm outline-none transition-colors"
                  style={{
                    backgroundColor: "#fff",
                    borderColor: "#d4c9b8",
                    color: "#3b2314",
                  }}
                />
              </div>
              <button
                type="submit"
                disabled={isSubmitting}
                className="px-5 py-3 rounded-xl text-sm font-semibold transition-opacity disabled:opacity-60 shrink-0"
                style={{ backgroundColor: "#3b2314", color: "#f5ecd7" }}
              >
                {isSubmitting ? "..." : "Subscribe"}
              </button>
            </form>
          </div>
        )}
        {/* Divider */}
        <div
          className="h-px w-16 mx-auto"
          style={{ backgroundColor: "#d4c9b8" }}
        />

        {/* Social links */}
        <div className="space-y-3">
          <p className="text-sm" style={{ color: "#6b5440" }}>
            Follow us on socials for updates:
          </p>
          <div className="flex items-center justify-center gap-5">
            <a
              href="https://instagram.com/groundedtheapp"
              target="_blank"
              rel="noopener noreferrer"
              aria-label="Instagram"
              className="transition-opacity hover:opacity-70"
              style={{ color: "#3b2314" }}
            >
              <Instagram className="h-5 w-5" />
            </a>
            <a
              href="https://tiktok.com/@groundedtheapp"
              target="_blank"
              rel="noopener noreferrer"
              aria-label="TikTok"
              className="transition-opacity hover:opacity-70"
              style={{ color: "#3b2314" }}
            >
              <svg className="h-5 w-5" viewBox="0 0 24 24" fill="currentColor">
                <path d="M19.59 6.69a4.83 4.83 0 0 1-3.77-4.25V2h-3.45v13.67a2.89 2.89 0 0 1-2.88 2.5 2.89 2.89 0 0 1-2.89-2.89 2.89 2.89 0 0 1 2.89-2.89c.28 0 .54.04.79.1v-3.49a6.37 6.37 0 0 0-.79-.05A6.34 6.34 0 0 0 3.15 15a6.34 6.34 0 0 0 6.34 6.34 6.34 6.34 0 0 0 6.34-6.34V8.98a8.21 8.21 0 0 0 4.76 1.52V7.06a4.84 4.84 0 0 1-1-.37z" />
              </svg>
            </a>
            <a
              href="https://threads.net/@groundedtheapp"
              target="_blank"
              rel="noopener noreferrer"
              aria-label="Threads"
              className="transition-opacity hover:opacity-70"
              style={{ color: "#3b2314" }}
            >
              <svg className="h-5 w-5" viewBox="0 0 24 24" fill="currentColor">
                <path d="M12.186 24h-.007C5.461 23.956.057 18.471 0 11.735 0 5.201 5.373 0 12 0c6.579 0 11.933 5.14 12 11.553v.181c0 4.03-2.304 6.535-4.18 7.672a7.862 7.862 0 0 1-1.752.796c-.25.08-.47.135-.638.17l-.077.015a.638.638 0 0 1-.724-.49.626.626 0 0 1 .474-.738l.062-.014c.127-.027.305-.072.516-.14a6.62 6.62 0 0 0 1.478-.67C20.703 17.24 22.75 15.133 22.75 11.734v-.163C22.69 5.862 17.875 1.25 12 1.25 6.076 1.25 1.25 5.897 1.25 11.735c.05 6.043 4.832 10.867 10.93 10.915 1.136 0 2.14-.158 3.058-.482a.625.625 0 1 1 .418 1.178 11.09 11.09 0 0 1-3.47.554zm4.137-8.721c-.148-3.653-2.653-5.656-5.804-5.82a6.267 6.267 0 0 0-3.474.824.625.625 0 1 0 .636 1.076 5.03 5.03 0 0 1 2.78-.656c2.478.13 4.358 1.588 4.487 4.564.028.283.04.578.04.882 0 2.263-.662 4.003-1.862 4.897a3.858 3.858 0 0 1-2.268.711c-1.043 0-1.903-.332-2.49-.96-.575-.617-.89-1.49-.89-2.463 0-1.628.953-2.755 2.316-2.755.54 0 1.005.18 1.31.508.282.303.415.712.374 1.15a.625.625 0 1 0 1.246.108c.065-.733-.145-1.424-.593-1.906-.48-.517-1.176-.81-1.962-.81h-.004c-1.964 0-3.562 1.598-3.562 3.705 0 1.27.418 2.41 1.178 3.222.79.845 1.905 1.301 3.227 1.301 1.1 0 2.12-.33 2.94-.95 1.473-1.115 2.253-3.12 2.253-5.633a9.6 9.6 0 0 0-.037-.997z" />
              </svg>
            </a>
          </div>
        </div>

        {/* Footer */}
        <p className="text-sm flex items-center justify-center gap-1.5" style={{ color: "#6b5440" }}>
          Made with <Heart className="h-3.5 w-3.5 fill-current" /> by remote workers, for remote workers.
        </p>
      </div>
    </main>
  );
}
